import { useState } from 'react';
import { Button, Flex, Input, Space, Tag } from 'antd';
import { FiCheck, FiPlus, FiX } from 'react-icons/fi';

import BaseText from '@components/typography/BaseText';
import { useAnnotationSessionStore } from '@stores/useAnnotationSessionStore';
import { annotationSource, selectBox } from '@utils/map/annotation/annotation.interaction';

import { boxesOf, labelsOn, relabelBox } from '../../ai/annotationSession';

const DEFAULT = null;
const eyebrowStyle = { textTransform: 'uppercase' };

/**
 * The labels of the active object, under its row in the annotation tree: a
 * chip per known label plus Default, and a way to add one. A chip sets the
 * label new boxes get ("Next box"); while a box is selected on the map the
 * card is about that box instead ("Selected box": a chip relabels it, and once
 * its label differs from what it had when selected, Done deselects it). Labels
 * are local state until a tile save sends them; the ones the summary lists are
 * offered too.
 *
 * Tier 1: `Tag.CheckableTag`, a dashed `Button` and a small `Input`.
 *
 * @param {object} props.object - the active taxonomy node
 */
const ObjectLabels = ({ object }) => {
	const stickyLabel = useAnnotationSessionStore(s => s.stickyLabelByNode[object.uuid] ?? DEFAULT);
	const addedLabels = useAnnotationSessionStore(s => s.labelsByNode[object.uuid]);
	const selectedBoxId = useAnnotationSessionStore(s => s.selectedBoxId);
	const setStickyLabel = useAnnotationSessionStore(s => s.setStickyLabel);
	const addLabel = useAnnotationSessionStore(s => s.addLabel);
	// Re-read the live boxes whenever the counts move (a draw, an erase, a relabel).
	useAnnotationSessionStore(s => s.boxesVersion);

	const [adding, setAdding] = useState(false);
	const [draft, setDraft] = useState('');

	const live = annotationSource ? boxesOf(annotationSource, object.uuid) : [];
	const selectedBox = selectedBoxId ? live.find(box => box.get('boxId') === selectedBoxId) : null;
	const editing = Boolean(selectedBox);

	const saved = useAnnotationSessionStore.getState().savedFor(object.uuid);
	// Labels the backend already knows for this object (the summary), the ones added here,
	// and the ones on boxes. The unlabelled entry (null) is the Default chip.
	const summaryLabels = (object.annotation_labels ?? [])
		.map(entry => entry.label)
		.filter(Boolean);
	const known = [
		...new Set([
			...summaryLabels,
			...(addedLabels ?? []),
			...labelsOn(live),
			...labelsOn(saved),
		]),
	];
	const current = editing ? (selectedBox.get('label') ?? DEFAULT) : stickyLabel;

	// The label the selected box had when it was selected: Done only shows once
	// the label differs from it (picking the original back hides it again).
	const [selection, setSelection] = useState({ boxId: null, label: DEFAULT });
	if (selectedBoxId !== selection.boxId && (selectedBox || !selectedBoxId)) {
		setSelection({ boxId: selectedBoxId, label: selectedBox?.get('label') ?? DEFAULT });
	}
	const relabelled = editing && selection.boxId === selectedBoxId && current !== selection.label;
	// A relabel changes the box, not React state; this re-renders the card for it.
	const [, setPicked] = useState(null);

	const pick = label => {
		if (editing) {
			if (annotationSource) relabelBox(annotationSource, selectedBoxId, label);
			setPicked(label);
			return;
		}
		setStickyLabel(object.uuid, label);
	};

	const commitDraft = () => {
		const label = addLabel(object.uuid, draft);
		if (label) pick(label);
		setAdding(false);
		setDraft('');
	};

	const cancelDraft = () => {
		setAdding(false);
		setDraft('');
	};

	const hint = editing
		? 'Click a chip to relabel this box. Saves with the tile.'
		: current
			? `New boxes are saved as ${object.name} with the label "${current}".`
			: `New boxes count as ${object.name} by default. Pick or add a label to annotate a variant.`;

	return (
		<Flex vertical gap="small" aria-label={`Labels for ${object.name}`}>
			<Flex align="center" gap="small">
				<BaseText size="xs" type="secondary" strong style={eyebrowStyle}>
					{editing ? 'Selected box' : `Next box: ${stickyLabel ?? 'Default'}`}
				</BaseText>
				{!adding && (
					<Button
						size="small"
						type="dashed"
						icon={<FiPlus />}
						onClick={() => setAdding(true)}
						aria-label="New label"
					/>
				)}
				{relabelled && (
					<Button
						size="small"
						type="primary"
						icon={<FiCheck />}
						onClick={() => selectBox(null)}
						style={{ marginLeft: 'auto' }}>
						Done
					</Button>
				)}
			</Flex>

			<Flex wrap gap="small" align="center">
				<Tag.CheckableTag checked={current === DEFAULT} onChange={() => pick(DEFAULT)}>
					Default
				</Tag.CheckableTag>
				{known.map(label => (
					<Tag.CheckableTag
						key={label}
						checked={current === label}
						onChange={() => pick(label)}>
						{label}
					</Tag.CheckableTag>
				))}
				{/* A label is added only on ✓ or Enter; ✕ or Esc drop it. Clicking away
				    adds nothing, so reaching for ✕ cannot add the label by mistake. */}
				{adding && (
					<Space.Compact>
						<Input
							size="small"
							autoFocus
							value={draft}
							placeholder="Label name"
							aria-label="Label name"
							onChange={event => setDraft(event.target.value)}
							onPressEnter={commitDraft}
							onKeyDown={event => {
								if (event.key !== 'Escape') return;
								// Dropping the draft unmounts this field before the session's Esc
								// handler (on the window) checks the focus, so it must not get here.
								event.stopPropagation();
								cancelDraft();
							}}
							style={{ width: 140 }}
						/>
						<Button
							size="small"
							type="primary"
							icon={<FiCheck />}
							onClick={commitDraft}
							disabled={!draft.trim()}
							aria-label="Add label"
						/>
						<Button
							size="small"
							icon={<FiX />}
							onClick={cancelDraft}
							aria-label="Cancel new label"
						/>
					</Space.Compact>
				)}
			</Flex>

			<BaseText size="xs" type="secondary">
				{hint}
			</BaseText>
		</Flex>
	);
};

export default ObjectLabels;
